import React from 'react';
import { Link } from 'react-router-dom';
import './Shared-Formatting.css';

function Article5() {
  return (
    <div className="article-wrapper">
      <article className="article-container">
        <h1 className="article-title">Probe Classification Expansion</h1>
        <div className="article-meta">
        <span>Last updated on: January 14th, 2026</span> | <span>Originally published on: January 13th, 2026</span> | <span>Author: Isaiah Milbank</span>
        </div>
        <div className="article-content">
          <p>
          Many probe representations that we care about might not naturally cover the input space. For example, in the Apollo deception work, we might not expect the model to represent "be honest" or "be deceptive" in every token.
          </p>
          <p>
          Do we gain or lose performance on our desired task by expanding the probe to classify tokens into classes non-overlapping with our desired class? How might our representations differ with the added information?
          </p>
          <p>
          We attempt to answer this question by looking at a fairly trivial probe: Whether text belongs to the User or the Assistant in chat logs.
          </p>
          <p>
          We use 100 examples from the UltraChat dataset with turn tokens inserted by the tokenizer for Llama-3.2-3b-Instruct tokenizer, the model we are probing. We extract activations from the residual stream after the layer 5 MLP, and classify each token as either "written by user", "written by model", or "special turn token".
          </p>
          <p>
          We then train a pair of logistic regression probes on the entire dataset, taking the first 60 prompts as our training set, 20 prompts as a validation set, and 20 prompts as a test set. We train the first pair with two classes on just the user/assistant prompts, disregarding the turn tokens, and train the second pair with three classes on all of the tokens. We find that they both achieve between 2 and 3 nines of accuracy on their respective evaluations, without much difference.
          </p>
          <p>
          We further investigated if there was much of a difference at lower training volume. We train on doubling sample sizes from 8 tokens to 4096 tokens, each on 5, 10, and 25 epochs. For each grouping of sample size, epoch, and classification size, we run a sweep of 30 runs on the learning rate, beta1, and beta2 for an Adam classifier.
          </p>
          <p>
          Since the total sample size is the same, the three class runs see fewer of the intended task tokens. This segment trends to 5.8 percent of the total training set as the sample size increases, but could take up a significantly larger portion of the smaller samples. A 5.8 percent difference might not be meaningfully noticeable in the accuracy of the probe, since they seem to converge fairly quickly.
          </p>

          <img src="/Pasted image 20260113181921.png" alt="Training performance comparison between two-class and three-class models" />

          <p>
          It appears that the two class model performs better or equal to the three class model in most of the training setups, but seems to converge as total training time increases (epochs * samples). Interestingly, we seem to see the most divergence in the 10 epoch runs, though only for those samples &lt; 256, and not consistently. Does this convergence in accuracy correspond to a convergence in representation?
          </p>

          <img src="/Pasted image 20260113183149.png" alt="Cosine similarity trend as training set size increases" />

          <p>
          It appears so! We find that the cosine similarity trends upwards as we increase the size of the training set.
          </p>
          <p>
          In summary, it seems we do not find a meaningful advantage towards either the limited or expanded classification structure as our probe approaches convergence. That said, there may be further advantages to diversifying our classifier. In a production environment, we may not be able to easily determine whether a given token falls within the desired task. It could be helpful, knowing this, to have probes with "other" classes, as it may improve our sensitivity/specificity curve.
          </p>
        </div>
        <footer className="article-footer">
          <p>Thanks for reading! Check out more articles in the <Link to="/articles">Articles</Link> section.</p>
        </footer>
      </article>
    </div>
  );
}

export default Article5;
